import { apiClient, ENDPOINTS, STORAGE_KEYS } from '@/lib/api/client';
import { getTenantIdFromToken, decodeJwtPayload } from '@/lib/auth/token-utils';
import { hydrateTenantSlugFromToken, resolveTenantId } from '@/lib/auth/tenant-id';
import type { LoginTenantInfo, TenantLoginResponse, TenantMeResponse } from '@/types/api';

export interface TenantSession {
  token: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  email?: string;
}

export function getTenantToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.tenantToken);
}

export function getTenantSession(): TenantSession | null {
  hydrateTenantSlugFromToken();
  const token = getTenantToken();
  const tenantId = resolveTenantId();
  if (!token || !tenantId) return null;

  const jwtTenantId = getTenantIdFromToken(token);
  if (!jwtTenantId || jwtTenantId !== tenantId) return null;

  const jwt = decodeJwtPayload(token);
  const tenantSlug =
    localStorage.getItem(STORAGE_KEYS.tenantSlug) || jwt?.tenant_slug || '';
  const tenantName =
    localStorage.getItem(STORAGE_KEYS.tenantName) || tenantSlug || 'Tenant';

  if (!tenantSlug) return null;

  return {
    token,
    tenantId,
    tenantName,
    tenantSlug,
    email: localStorage.getItem('tenant_email') ?? undefined,
  };
}

export function persistTenantSession(
  token: string,
  tenant: Pick<LoginTenantInfo, 'tenant_id' | 'tenant_name' | 'tenant_slug'>,
  email?: string,
): void {
  localStorage.setItem(STORAGE_KEYS.tenantToken, token);
  localStorage.setItem(STORAGE_KEYS.tenantId, tenant.tenant_id);
  localStorage.setItem(STORAGE_KEYS.tenantName, tenant.tenant_name);
  localStorage.setItem(STORAGE_KEYS.tenantSlug, tenant.tenant_slug);
  if (email) {
    localStorage.setItem('tenant_email', email);
  }
}

export function clearTenantSession(): void {
  localStorage.removeItem(STORAGE_KEYS.tenantToken);
  localStorage.removeItem(STORAGE_KEYS.tenantId);
  localStorage.removeItem(STORAGE_KEYS.tenantName);
  localStorage.removeItem(STORAGE_KEYS.tenantSlug);
  localStorage.removeItem('tenant_email');
}

export function isTenantAuthenticated(): boolean {
  const token = getTenantToken();
  if (!token) return false;
  const storedTenantId = localStorage.getItem(STORAGE_KEYS.tenantId);
  const jwtTenantId = getTenantIdFromToken(token);
  return Boolean(storedTenantId && jwtTenantId && storedTenantId === jwtTenantId);
}

export interface TenantRegisterPayload {
  business_name: string;
  contact_email: string;
  website_url: string;
  password: string;
  industry: string;
}

export interface TenantRegisterResponse {
  status: string;
  tenant_id: string;
  tenant_slug: string;
  message: string;
  dashboard_url: string;
  email_verification_sent: boolean;
}

export async function registerTenant(
  payload: TenantRegisterPayload,
): Promise<TenantRegisterResponse> {
  return apiClient.post<TenantRegisterResponse>(ENDPOINTS.tenant.register, payload, {
    skipAuth: true,
    portal: null,
  });
}

export async function loginTenant(login: string, password: string): Promise<TenantLoginResponse> {
  const ident = login.trim();
  const response = await apiClient.post<TenantLoginResponse>(
    ENDPOINTS.tenant.login,
    { login: ident, email: ident, username: ident, password },
    { skipAuth: true, portal: null },
  );

  const primaryTenant = response.tenants[0];
  if (!primaryTenant) {
    throw new Error('No active tenants found for this account.');
  }

  persistTenantSession(response.token, primaryTenant, response.email);
  return response;
}

export async function fetchTenantMe(): Promise<TenantMeResponse> {
  return apiClient.get<TenantMeResponse>(ENDPOINTS.tenant.me, { portal: 'tenant' });
}

export function logoutTenant(): void {
  clearTenantSession();
}
